/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo } from 'react';
import { injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import {
  ValueCard,
  ImageCard,
  TimeSeriesCard,
  TableCard,
  BarChartCard,
  ListCard,
  GaugeCard,
  Card,
  getCardVariables,
  CARD_TYPES,
  CARD_SIZES,
} from 'carbon-addons-iot-react';
import { useDebounce } from 'use-debounce';

import {
  CUSTOM_CARD_TYPES,
  DataItemsPropTypes,
  MOMENT_INPUT_FORMATS,
} from '../../constants/constants';
import ThirdPartyCard from '../../../Dashboard/components/ThirdPartyCard/ThirdPartyCard';

import { cardDataMapper, mapAdditionalData, getTimeInterval } from 'metricsToCards';

const propTypes = {
  // this will be different for every card type until we split the mappers into individual card mappers,
  cardProps: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  /** Use the data to dynamically update the card variables */
  data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  /** pass the additional data to update the card variables */
  additionalData: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  /** the deviceid id of the current dashboard only valid for instance dashboards */
  deviceId: PropTypes.string,
  /** needs to know if an instance dashboard exists because the card will render differently */
  hasInstanceDashboard: PropTypes.bool,
  /** needs the data items to map values with the correct format */
  dataItems: DataItemsPropTypes,
  /** needs the name of the entityType to correctly map the instance dashboard links */
  entityType: PropTypes.string,
  /** needed to show x-axis in the correct format */
  grain: PropTypes.string,
  /** need to pass to third party content card for context */
  startDate: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  endDate: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

const defaultProps = {
  data: [],
  additionalData: [],
  hasInstanceDashboard: false,
  deviceId: null,
  entityType: null,
  dataItems: [],
  grain: 'minute',
  startDate: null,
  endDate: null,
};

/**
 * Takes the table's height in pixels and converts it to rem, removing the height of the card title and pagination
 * @param {string} pixels (ex: '512px')
 */
export const getTableCardContentHeight = heightInPixels => {
  const heightInteger = Number(heightInPixels.slice(0, heightInPixels.length - 2));
  return `${heightInteger / 16 - 6 || 13}rem`;
};

/**
 * This component dynamically updates the full card based on the data that was passed and renders the card
 */
const CardMapper = ({
  cardProps,
  data,
  additionalData,
  hasInstanceDashboard,
  deviceId,
  dataItems,
  entityType,
  grain,
  startDate,
  endDate,
  intl: { locale, timeZone },
}) => {
  // The most data points a card can ever show is around 3000 before the performance dies so if we're here we need to change the label and limit the range selections
  const { i18n, timeRange: timeRangeProp, timeRangeOptions: timeRangeOptionsProp } = cardProps;
  const timeRange = timeRangeProp;
  const timeRangeOptions = timeRangeOptionsProp;
  const language = locale.split('-')[0];

  const cardVariables = useMemo(() => getCardVariables({ ...cardProps, data }), [cardProps, data]);
  // First map the card data from the prop
  const updatedCardWithData = useMemo(
    () =>
      isEmpty(data) && cardProps.type !== CARD_TYPES.IMAGE // image cards need to map data for labels only
        ? cardProps
        : cardDataMapper(
            cardProps,
            data,
            hasInstanceDashboard,
            cardVariables,
            deviceId,
            dataItems,
            entityType,
            language,
            grain,
            timeZone
          ),
    [
      data,
      cardProps,
      hasInstanceDashboard,
      cardVariables,
      deviceId,
      dataItems,
      entityType,
      language,
      grain,
      timeZone,
    ]
  );

  // Then map the additional data from the prop
  const updatedCardWithAdditionalData = useMemo(
    () =>
      isEmpty(additionalData)
        ? updatedCardWithData
        : mapAdditionalData(updatedCardWithData, additionalData, dataItems),
    [additionalData, dataItems, updatedCardWithData]
  );

  // PAL issue created to handle this on PAL side https://github.com/carbon-design-system/carbon-addons-iot-react/issues/3229#issue-1114342029
  const interval = useMemo(
    () =>
      updatedCardWithAdditionalData.type === CARD_TYPES.TIMESERIES
        ? { interval: getTimeInterval(updatedCardWithAdditionalData?.values) }
        : {},
    [updatedCardWithAdditionalData.type, updatedCardWithAdditionalData?.values]
  );

  const commonCardProps = useMemo(
    () => ({
      ...updatedCardWithAdditionalData, // pass all the card props, including the card data to the card
      key: cardProps.id,
      i18n,
      locale: language,
      timeRange,
      ...interval,
      timeRangeOptions,
    }),
    [
      updatedCardWithAdditionalData,
      cardProps.id,
      i18n,
      language,
      timeRange,
      interval,
      timeRangeOptions,
    ]
  );

  // debounce commonCardProps to improve performance on large amounts of data for Timeseries and Barchart cards
  const [debouncedCommonProps] = useDebounce(commonCardProps, 300);
  const largeDataCardProps = data?.length > 300 ? debouncedCommonProps : commonCardProps;

  switch (cardProps.type) {
    case CARD_TYPES.VALUE:
      return <ValueCard {...commonCardProps} />;
    case CARD_TYPES.IMAGE:
      return <ImageCard {...commonCardProps} error={cardProps.setupError || cardProps.error} />;
    case CARD_TYPES.TIMESERIES:
      return (
        <TimeSeriesCard
          {...largeDataCardProps}
          tooltipShowTotals={false}
          defaultDateFormatPattern={MOMENT_INPUT_FORMATS.SECONDS}
        />
      );
    case CARD_TYPES.TABLE: {
      // TableCardSizes smaller than LARGE are not officially supported by PAL and cause issues with the row count,
      // so we need to allow the table to scroll in that case (we are allowing MEDIUM, MEDIUMTHIN, and MEDIUMWIDE through)
      const additionalTableStyles =
        cardProps.type === CARD_TYPES.TABLE &&
        (commonCardProps.size === CARD_SIZES.MEDIUM ||
          commonCardProps.size === CARD_SIZES.MEDIUMTHIN ||
          commonCardProps.size === CARD_SIZES.MEDIUMWIDE)
          ? {
              tableHeight: getTableCardContentHeight(commonCardProps.style?.height),
              shouldScroll: 'auto',
            }
          : {};

      return (
        <TableCard
          {...commonCardProps}
          style={{
            ...commonCardProps?.style,
            '--table-content-height': additionalTableStyles.tableHeight,
            '--should-scroll': additionalTableStyles.shouldScroll,
          }}
          defaultDateFormatPattern={MOMENT_INPUT_FORMATS.SECONDS}
        />
      );
    }
    case CARD_TYPES.BAR:
      return (
        <BarChartCard
          {...largeDataCardProps}
          defaultDateFormatPattern={MOMENT_INPUT_FORMATS.SECONDS}
        />
      );
    case CARD_TYPES.LIST:
      return (
        <ListCard
          {...commonCardProps}
          data={cardProps.content.data}
          loadData={cardProps.content.loadData}
        />
      );
    case CARD_TYPES.GAUGE:
      return <GaugeCard {...commonCardProps} />;
    case CARD_TYPES.CUSTOM:
    case CUSTOM_CARD_TYPES.THIRD_PARTY:
      return (
        <ThirdPartyCard {...commonCardProps} data={data} startDate={startDate} endDate={endDate} />
      );
    case CUSTOM_CARD_TYPES.ALERT:
      return (
        // we need to pass the data prop to custom cards so it can be passed down to the content section
        // These props are given to the cardProps.content function as the second argument
        <Card hideHeader={isNil(cardProps.title)} {...omit(commonCardProps, 'content')} data={data}>
          {commonCardProps.content}
        </Card>
      );
    default:
      return null;
  }
};

CardMapper.propTypes = propTypes;
CardMapper.defaultProps = defaultProps;
export default injectIntl(CardMapper);
/* eslint-enable react/jsx-props-no-spreading */
